import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi } from './api';

/**
 * INTELLIGENCE TERRITORIALE (§19, §31-§36, §37).
 *
 * ⚠️⚠️ CET ÉCRAN MONTRE D'ABORD CE QU'IL NE SAIT PAS, ET CE N'EST PAS DE LA
 * MODESTIE. Les quatre facteurs qui décrivent le métier du cadastre — titres,
 * renseignement, revendication, parcellaire levé — valent ZÉRO sur les 462
 * divisions (mesuré le 10/10/2026). Un écran qui afficherait un score sans le
 * dire laisserait croire que le territoire a été jugé sur son foncier, alors
 * qu'il l'aurait été sur de la densité de bâti importée d'OSM.
 *
 * ⚠️ LE BANDEAU D'AVEUX EST EN HAUT, PAS EN BAS. Mesuré ailleurs dans ce
 * dépôt : une fraîcheur reléguée dans un coin d'écran n'est jamais lue. Un
 * aveu de même.
 *
 * ⚠️ UNE PASTILLE ⚪ N'EST PAS UNE MAUVAISE NOTE. L'écran l'écrit en toutes
 * lettres à côté de la légende : sans cette phrase, un lecteur range le blanc
 * avec le rouge, et conclut que le territoire va mal là où on ne sait rien.
 */

type Onglet = 'etat' | 'radar' | 'observation' | 'signaux';

const ONGLETS: Array<{ cle: Onglet; libelle: string; quoi: string }> = [
  { cle: 'etat', libelle: "Mode État", quoi: '§37 — les échelons national, régional, local' },
  { cle: 'radar', libelle: 'Radar', quoi: '§32 — la carte des six états' },
  { cle: 'observation', libelle: 'Observatoire', quoi: '§19 — les faits et leur population' },
  { cle: 'signaux', libelle: 'Signaux', quoi: '§36 — ce qui est détectable' },
];

const NIVEAUX: Record<number, string> = {
  1: 'Régions', 2: 'Préfectures', 3: 'Communes', 4: 'Sous-préfectures et quartiers',
};

function nb(v: unknown): string {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n.toLocaleString('fr-FR') : '—';
}

/**
 * ⚠️ `null` ET `0` NE S'AFFICHENT PAS PAREIL, ET C'EST TOUT LE SUJET. Le
 * service rend `valeur: null` quand il n'a rien eu à mesurer et `0` quand la
 * mesure a eu lieu et rend zéro. Les écrire tous les deux « 0 » effacerait la
 * seule distinction que ce module existe pour tenir.
 */
function valeurOuAbsence(v: number | null, unite: string, motif?: string) {
  if (v === null) {
    return <em className="sans-matiere" title={motif ?? 'aucune matière'}>non mesurable</em>;
  }
  return <span>{v.toLocaleString('fr-FR')} <small>{unite}</small></span>;
}

function Fraicheur({ d }: { d: { calculee_le: string | null; age_heures: number | null; perimee: boolean } }) {
  if (!d.calculee_le) {
    return <p className="avertissement">L’observatoire n’a jamais été calculé — aucun chiffre ci-dessous ne vaut.</p>;
  }
  return (
    <p className={d.perimee ? 'avertissement' : 'discret'}>
      Calculé le {new Date(d.calculee_le).toLocaleString('fr-FR')}
      {d.age_heures !== null && <> · il y a {d.age_heures} h</>}
      {d.perimee && <> — <strong>périmé</strong> : rafraîchissez avant de décider.</>}
    </p>
  );
}

function Aveux({ aveux }: { aveux: Array<{ section: string; demande: string; manque: string; mesure: string }> }) {
  const [ouvert, setOuvert] = useState(false);
  if (!aveux?.length) return null;
  return (
    <section className="carte aveux">
      <button className="bouton discret" onClick={() => setOuvert(!ouvert)}>
        {ouvert ? '▾' : '▸'} {aveux.length} capacité(s) demandée(s) par le cahier des charges
        et <strong>sans matière en base</strong>
      </button>
      {ouvert && (
        <table className="tableau">
          <thead><tr><th>Section</th><th>Demandé</th><th>Ce qui manque</th><th>La mesure</th></tr></thead>
          <tbody>
            {aveux.map((a) => (
              <tr key={a.section}>
                <td><strong>{a.section}</strong></td>
                <td>{a.demande}</td>
                <td>{a.manque}</td>
                {/* La MESURE est rendue, pas la conclusion : une conclusion ne
                    se réfute pas, une commande se rejoue. */}
                <td><code>{a.mesure}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function Intelligence() {
  const [onglet, setOnglet] = useState<Onglet>('etat');
  const [niveau, setNiveau] = useState(2);
  const [donnees, setDonnees] = useState<any>(null);
  const [score, setScore] = useState<any>(null);
  const [rapport, setRapport] = useState<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [charge, setCharge] = useState(false);
  const [travail, setTravail] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setCharge(true); setErreur(null); setScore(null); setRapport(null);
    try {
      const r = onglet === 'etat' ? await api.intelligence.etat()
        : onglet === 'radar' ? await api.intelligence.radar(niveau)
        : onglet === 'observation' ? await api.intelligence.observation(niveau)
        : await api.intelligence.signaux();
      setDonnees(r);
    } catch (e) {
      // ⚠️ Un observatoire jamais calculé rend 404 avec SA phrase : la relayer
      // telle quelle, parce qu'elle nomme le geste à faire. Un « erreur de
      // chargement » générique enverrait chercher une panne.
      setErreur(e instanceof ErreurApi ? e.message : String(e));
      setDonnees(null);
    } finally { setCharge(false); }
  }, [onglet, niveau]);

  useEffect(() => { void charger(); }, [charger]);

  const rafraichir = async () => {
    setTravail('Recalcul en cours — la couverture puis l’observatoire (≈ 30 s)…');
    try {
      const r = await api.intelligence.rafraichir();
      // ⚠️ LES DEUX RÉSULTATS SONT DITS. Un « fait » unique laisserait croire
      // que tout est à jour alors qu'une des deux vues a pu refuser.
      const bouts = [
        r.couverture_erreur ? `couverture EN ÉCHEC : ${r.couverture_erreur}` : `couverture ${r.couverture_le ? 'à jour' : 'inchangée'}`,
        r.observatoire_erreur ? `observatoire EN ÉCHEC : ${r.observatoire_erreur}` : `observatoire ${r.observatoire_le ? 'à jour' : 'inchangé'}`,
      ];
      setTravail(bouts.join(' · '));
      await charger();
    } catch (e) {
      setTravail(e instanceof ErreurApi ? e.message : String(e));
    }
  };

  const voirScore = async (divisionId: string) => {
    try { setScore(await api.intelligence.score(divisionId)); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String(e)); }
  };

  const voirRapport = async (divisionId: string) => {
    try { setRapport(await api.intelligence.rapport(divisionId)); setScore(null); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String(e)); }
  };

  return (
    <div className="ecran intelligence">
      <header className="ecran-tete">
        <div>
          <h2>Intelligence territoriale</h2>
          <p className="discret">
            {ONGLETS.find((o) => o.cle === onglet)!.quoi}
          </p>
        </div>
        <button className="bouton" onClick={rafraichir}>Recalculer l’observatoire</button>
      </header>

      {travail && <p className="avertissement">{travail}</p>}

      <nav className="onglets">
        {ONGLETS.map((o) => (
          <button key={o.cle} className={`onglet ${onglet === o.cle ? 'actif' : ''}`}
                  onClick={() => setOnglet(o.cle)}>{o.libelle}</button>
        ))}
        {(onglet === 'radar' || onglet === 'observation') && (
          <select value={niveau} onChange={(e) => setNiveau(Number(e.target.value))}>
            {Object.entries(NIVEAUX).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        )}
      </nav>

      {erreur && <p className="erreur">{erreur}</p>}
      {charge && <p className="discret">Chargement…</p>}

      {donnees && (
        <>
          <Fraicheur d={donnees} />
          <Aveux aveux={donnees.aveux} />

          {onglet === 'etat' && (
            <section className="carte">
              <h3>Les échelons</h3>
              <table className="tableau">
                <thead><tr>
                  <th>Échelon</th><th>Divisions</th><th>Bâtiments</th><th>Parcelles</th>
                  <th>Surface</th><th>Notées</th><th>Sans note</th><th>Note médiane</th><th>Lecture</th>
                </tr></thead>
                <tbody>
                  {donnees.echelons.map((e: any) => (
                    <tr key={e.niveau}>
                      <td><strong>{NIVEAUX[e.niveau] ?? e.niveau}</strong></td>
                      <td>{nb(e.divisions)}</td><td>{nb(e.batiments)}</td><td>{nb(e.parcelles)}</td>
                      <td>{nb(e.surface_km2)} km²</td>
                      <td>{nb(e.notees)}</td>
                      <td>{nb(e.sans_note)}</td>
                      {/* ⚠️ « — » et non « 0 » : une médiane absente n'est pas
                          une médiane nulle. */}
                      <td>{e.note_mediane === null ? <em>—</em> : `${e.note_mediane}/100`}</td>
                      <td className="lecture">{e.lecture}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {onglet === 'radar' && (
            <section className="carte">
              <h3>Radar — {NIVEAUX[donnees.niveau]}</h3>
              <p className="discret">
                Rang pris sur les <strong>{nb(donnees.population_du_rang)}</strong> division(s) de
                cet échelon ; <strong>{nb(donnees.rendues)}</strong> dans votre portée.
                Note minimale de matière : {donnees.poids_minimum}/{donnees.poids_total} points de poids.
              </p>
              <ul className="legende">
                {donnees.legende.map((l: any) => (
                  <li key={l.classement}>{l.pastille} {l.classement.replace(/_/g, ' ')} — <strong>{l.n}</strong></li>
                ))}
              </ul>
              <p className="avertissement">
                ⚪ « données insuffisantes » n’est <strong>pas</strong> une mauvaise note : c’est
                l’absence de note. Ces divisions ne sont pas en difficulté — on ne sait pas
                encore les juger, et les ranger avec les 🔴 serait un diagnostic inventé.
              </p>
              {donnees.timeline === null && (
                <p className="discret">
                  La frise 2026 → 2050 du §32 n’est pas servie : le registre ne porte aucune date
                  de construction (voir les aveux).
                </p>
              )}
              <table className="tableau">
                <thead><tr>
                  <th></th><th>Division</th><th>Note</th><th>Confiance</th>
                  <th>Facteurs notés</th><th>Écartés</th><th></th>
                </tr></thead>
                <tbody>
                  {donnees.divisions.map((d: any) => (
                    <tr key={d.division_id}>
                      <td>{d.pastille}</td>
                      <td>{d.division}</td>
                      <td>{d.note === null ? <em>pas de note</em> : `${d.note}/100`}</td>
                      <td>{d.confiance} %</td>
                      <td>{d.facteurs_notes}</td>
                      <td>{d.facteurs_ecartes}</td>
                      <td>
                        <button className="bouton discret" onClick={() => voirScore(d.division_id)}>Pourquoi ?</button>
                        <button className="lien-action" onClick={() => voirRapport(d.division_id)}>Rapport</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {onglet === 'observation' && (
            <section className="carte">
              <h3>Les indicateurs et leur population</h3>
              <p className="discret">
                Socle : {nb(donnees.socle.batiments)} bâtiments · {nb(donnees.socle.parcelles)} parcelles ·
                {' '}{nb(donnees.socle.surface_km2)} km² · {nb(donnees.socle.divisions)} divisions
                ({donnees.portee}).
              </p>
              <table className="tableau">
                <thead><tr>
                  <th>Indicateur</th><th>Sens</th><th>Poids</th>
                  <th>Divisions mesurables</th><th>Dont porteuses</th><th>Total</th><th>Source</th>
                </tr></thead>
                <tbody>
                  {donnees.indicateurs.map((i: any) => (
                    <tr key={i.code} className={i.aveu ? 'sans-matiere-ligne' : ''}>
                      <td><strong>{i.libelle}</strong><br /><small>{i.unite}</small></td>
                      <td>{i.sens === 'contexte' ? 'contexte (non noté)' : i.sens}</td>
                      <td>{i.poids === 0 ? <em>—</em> : i.poids}</td>
                      <td>{nb(i.divisions_mesurables)}</td>
                      <td>{nb(i.divisions_porteuses)}</td>
                      <td>{nb(i.total_numerateur)}</td>
                      <td><small>{i.source}</small>{i.aveu && <><br /><em className="sans-matiere">{i.aveu}</em></>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3>Les divisions</h3>
              {donnees.tronque > 0 && (
                <p className="discret">{nb(donnees.tronque)} division(s) de plus ne sont pas affichées.</p>
              )}
              <table className="tableau">
                <thead><tr>
                  <th>Division</th><th>Bâtiments</th><th>Parcelles</th><th>Densité bâtie</th>
                  <th>Emprise au sol</th><th>Maillage voirie</th><th>Bâti rattaché</th>
                </tr></thead>
                <tbody>
                  {donnees.divisions.map((d: any) => {
                    const m = (code: string) => d.mesures.find((x: any) => x.code === code);
                    return (
                      <tr key={d.division_id}>
                        <td><strong>{d.division}</strong>{d.descendants > 0 && <small> · {d.descendants} sous-division(s)</small>}</td>
                        <td>{nb(d.faits.batiments)}</td>
                        <td>{nb(d.faits.parcelles)}</td>
                        <td>{valeurOuAbsence(m('densite_batie').valeur, m('densite_batie').unite, m('densite_batie').sans_matiere)}</td>
                        <td>{valeurOuAbsence(m('emprise_au_sol').valeur, m('emprise_au_sol').unite, m('emprise_au_sol').sans_matiere)}</td>
                        <td>{valeurOuAbsence(m('maillage_voirie').valeur, m('maillage_voirie').unite, m('maillage_voirie').sans_matiere)}</td>
                        <td>{valeurOuAbsence(m('rattachement_parcellaire').valeur, m('rattachement_parcellaire').unite, m('rattachement_parcellaire').sans_matiere)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          {onglet === 'signaux' && (
            <section className="carte">
              <h3>Signaux — {nb(donnees.population)} division(s) examinée(s)</h3>
              {!donnees.detection_temporelle.disponible && (
                <p className="avertissement">
                  La détection par <strong>évolution</strong> (§36) n’est pas servie :
                  {' '}{donnees.detection_temporelle.motif}<br />
                  <code>{donnees.detection_temporelle.mesure}</code>
                </p>
              )}
              {donnees.signaux.map((s: any) => (
                <article key={s.code} className={`signal ${s.gravite}`}>
                  <h4>{s.libelle} <span className="jeton">{s.gravite}</span></h4>
                  {/* ⚠️ LE COMPTE NE VA JAMAIS SANS SA POPULATION. « 0 division
                      touchée » est trivialement vrai sur un ensemble vide, et
                      se lit exactement comme « tout va bien ». */}
                  <p>
                    <strong>{nb(s.n)}</strong> division(s) sur <strong>{nb(s.population)}</strong> examinée(s)
                    {s.population === 0 && <em> — la règle n’a rien eu à examiner : ce n’est pas un résultat</em>}
                  </p>
                  <p className="discret">{s.lecture}</p>
                  {s.divisions.length > 0 && (
                    <ul className="liste-plate">
                      {s.divisions.slice(0, 10).map((d: any) => (
                        <li key={d.division_id}>{d.division} — {nb(d.valeur)}</li>
                      ))}
                      {s.n > 10 && <li className="discret">… et {nb(s.n - 10)} autre(s)</li>}
                    </ul>
                  )}
                </article>
              ))}
            </section>
          )}
        </>
      )}

      {rapport && (
        <section className="carte rapport-territorial">
          <header className="ecran-tete">
            <div>
              <h3>Rapport territorial — {rapport.division}</h3>
              {/* ⚠️ DEUX DATES, ET LES CONFONDRE EST LE DÉFAUT. Un rapport émis
                  aujourd'hui sur des chiffres d'il y a un mois doit le dire :
                  sinon la date d'émission se lit comme la date des chiffres. */}
              <p className="discret">
                Émis le {new Date(rapport.emis_le).toLocaleString('fr-FR')} ·
                {' '}chiffres de l’observatoire{' '}
                {rapport.observatoire_le
                  ? `du ${new Date(rapport.observatoire_le).toLocaleString('fr-FR')}`
                  : <strong>jamais calculés</strong>}
              </p>
            </div>
            <button className="bouton discret" onClick={() => setRapport(null)}>Fermer</button>
          </header>

          <p className="avertissement">{rapport.avertissement}</p>

          {rapport.sections.map((s: any) => (
            <article key={s.titre} className="rapport-section">
              <h4>{s.titre}</h4>
              <ul className="liste-plate">
                {s.lignes.map((l: string, i: number) => <li key={i}>{l}</li>)}
              </ul>
              {/* ⚠️ « Aucune réserve » s’ÉCRIT. Une section sans réserve
                  affichée se lit comme une section sans réserve, et les deux
                  ne sont pas la même chose. */}
              {s.reserves.length > 0 ? (
                <ul className="liste-plate reserves">
                  {s.reserves.map((r: string, i: number) => <li key={i}>⚠️ {r}</li>)}
                </ul>
              ) : (
                <p className="discret">Aucune réserve sur cette section.</p>
              )}
            </article>
          ))}

          {/* ⚠️⚠️ LES MENTIONS LÉGALES SONT DANS LE DOCUMENT, PAS EN BAS DE
              L’ÉCRAN. Ce rapport se copie et se transmet ; une attribution
              laissée dans le pied de page de l’atelier ne voyage pas avec lui,
              et l’art. 9 de la licence Copernicus fait perdre les droits en cas
              de manquement. */}
          <h4>Sources et mentions légales</h4>
          {rapport.attributions.length === 0 ? (
            <p className="erreur">
              Aucune mention légale servie — ce document ne doit pas être diffusé.
            </p>
          ) : (
            <ul className="liste-plate">
              {rapport.attributions.map((a: string, i: number) => <li key={i}>{a}</li>)}
            </ul>
          )}
          <Aveux aveux={rapport.aveux} />
        </section>
      )}

      {score && (
        <section className="carte score-detail">
          <h3>{score.pastille} {score.division} — {score.note === null ? 'pas de note' : `${score.note}/100`}</h3>
          <p className="lecture">{score.lecture}</p>

          <h4>Les facteurs qui ont compté</h4>
          {score.facteurs.length === 0 ? <p className="discret">Aucun.</p> : (
            <table className="tableau">
              <thead><tr><th>Facteur</th><th>Valeur</th><th>Position</th><th>Pairs</th><th>Poids</th><th>Source</th></tr></thead>
              <tbody>
                {score.facteurs.map((f: any) => (
                  <tr key={f.code}>
                    <td>{f.libelle}</td>
                    <td>{f.valeur} <small>{f.unite}</small></td>
                    <td>{f.position}/100 {f.sens === 'defavorable' && <small>(sens inversé)</small>}</td>
                    <td>{f.pairs}</td><td>{f.poids}</td>
                    <td><small>{f.source}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h4>Les facteurs ÉCARTÉS, et pourquoi</h4>
          {score.ecartes.length === 0 ? <p className="discret">Aucun : la note porte sur tous les facteurs notables.</p> : (
            <ul className="liste-plate">
              {score.ecartes.map((e: any) => (
                <li key={e.code}><strong>{e.libelle}</strong> — {e.motif}</li>
              ))}
            </ul>
          )}

          <h4>Contexte (affiché, jamais noté)</h4>
          <ul className="liste-plate">
            {score.contexte.map((c: any) => (
              <li key={c.code}>{c.libelle} : {valeurOuAbsence(c.valeur, c.unite, c.sans_matiere)}</li>
            ))}
          </ul>
          <p className="discret">
            Dense n’est ni bien ni mal : noter le contexte aurait récompensé une capitale pour
            être une capitale.
          </p>

          <h4>Thèse territoriale (§34)</h4>
          <p className="avertissement">{score.these.avertissement}</p>
          {([['Pourquoi cette division', 'pourquoi'], ['Risques', 'risques'],
             ['Incertitudes', 'incertitudes'], ['Données manquantes', 'donnees_manquantes']] as const).map(([titre, cle]) => (
            <div key={cle}>
              <h5>{titre}</h5>
              <ul className="liste-plate">
                {score.these[cle].map((l: string, i: number) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          ))}
          <button className="bouton discret" onClick={() => setScore(null)}>Fermer</button>
        </section>
      )}
    </div>
  );
}
